import React from 'react';
import {
    get as _get,
    has as _has,
    isEqual as _isEqual,
    uniqWith as _uniqWith,
    isEmpty as _isEmpty,
} from 'lodash';
import moment from 'moment';

import Link from '@appComponents/Link';
import {Authorization} from '@appComponents/Authorization';
import {convertToInt} from '@utils/helpers';
import * as CONST from '@constants/variables';
import * as RESOURCES from '@constants/resources';
import {renderEventStatusName} from '@utils/status';
import {IconCheckmark, IconLinkify} from '@appComponents/IconCollection';
import LinkWithAuthorization from '@appComponents/LinkWithAuthorization';
import {renderDescription} from '@utils/tableHelpers';
import {ReportScopesPopup} from '@modules/propertyLicence/views/ReportScopesPopup';

const authorizationObject = {
    [RESOURCES.PROPERTY_LICENCE]: CONST.SECURITY_PRIVILEGES_READ,
};

export const columns = {
    'id': {
        label: 'ID',
        sorting: (row) => convertToInt(row.id),
        header: {
            align: 'center',
            isCollapsed: true,
            className: '--noPadding',
        },
        content: {
            align: 'center',
        },
    },
    'name': {label: 'Name', sorting: true},
    'licensor': {
        label: 'Licensor',
        sorting: true,
        header: {
            className: 'column--min-width-140',
        },
    },
    'licensees': {
        label: 'Licensee(s)',
        sorting: row => renderLicensees(row),
    },
    'property_licence_type': {
        label: 'Type',
        sorting: true,
        header: {
            align: 'left',
        },
        content: {
            align: 'left',
        },
    },
    'sport': {
        label: 'Sport / 1st level category',
        sorting: row => `${_get(row, 'sport.name', '')}${_get(row, 'content_category.name', '')}`,
    },
    'contents': {
        label: 'Content',
        sorting: row => row.contentSortValue,
        header: {
            className: 'column--min-width-250',
        },
    },
    'content_tier': {
        label: 'Tier',
        sorting: true,
        header: {
            align: 'left',
        },
        content: {
            align: 'left',
        },
    },
    'content_package_type': {
        label: 'Content package type',
        sorting: true,
        header: {
            align: 'left',
        },
        content: {
            align: 'left',
        },
    },
    'content_manager_user': {
        label: 'Content manager',
        sorting: true,
    },
    'property_licence_status': {
        label: 'Status',
        sorting: true,
    },
    'has_accreditation': {
        label: 'Accreditation?',
        sorting: true,
        header: {
            align: 'center',
        },
        content: {
            align: 'center',
        },
    },
    'actions': {
        label: 'Actions',
        header: {
            align: 'center',
        },
        content: {
            align: 'center',
        },
    },
};

export const getUniqueNames = (items) => {
    const uniqueNames = [];
    const nameSet = new Set();

    for (const item of items) {
        if (!nameSet.has(item.name)) {
            nameSet.add(item.name);
            uniqueNames.push(item.name);
        }
    }

    return uniqueNames.join(', ');
};

export const renderIdColumn = (row) => {
    const eventId = convertToInt(_get(row, 'id.value', null));

    return (!eventId
        ? ''
        :
        <LinkWithAuthorization
            authorization={{[RESOURCES.EVENT]: CONST.SECURITY_PRIVILEGES_READ}}
            link={{
                name: 'events.configuration.index',
                params: {id: eventId},
            }}
        >
            {eventId.toString()}
        </LinkWithAuthorization>);
};

export const renderMatchIdColumn = (column, row) => {
    const linkIcon = row.sr_event ? <IconLinkify color='red'/> : '';

    return (!_has(row, 'sr_event.match.id'))
        ? linkIcon
        : parseInt(row.sr_event.match.id, 10);
};

export const renderStartDatetimeColumn = (column, row) => {
    return (
        <>
            <div>
                {_isEmpty(row[column])
                    ? ''
                    : moment(row[column].value).format('YYYY-MM-DD HH:mm')
                }
            </div>
            <div>
                {_isEmpty(row.end_datetime)
                    ? ''
                    : moment(row.end_datetime).format('YYYY-MM-DD HH:mm')
                }
            </div>
        </>
    );
};

export const renderCategoryColumn = (row) => {
    let categoryColumnContent = null;

    if (_has(row, 'sr_event.match.tournament.tournament_category.id')) {
        categoryColumnContent = row.sr_event.match.tournament.tournament_category.name;
    } else if (_has(row, 'sr_event.tournament.tournament_category.id')) {
        categoryColumnContent = row.sr_event.tournament.tournament_category.name;
    }

    return categoryColumnContent;
};

export const renderTournamentColumn = (row) => {
    let tournamentColumnContent = null;

    if (_has(row, 'sr_event.match.tournament.id')) {
        tournamentColumnContent = row.sr_event.match.tournament.name;
    } else if (_has(row, 'sr_event.tournament.id')) {
        tournamentColumnContent = row.sr_event.tournament.name;
    }

    return tournamentColumnContent;
};

export const renderContentCategoryColumn = (row, level) => {
    return (!_has(row, `av_event.content_category_level${level}.id`))
        ? ''
        : row.av_event[`content_category_level${level}`].name;
};

export const renderCompetitorsColumn = (row) => {
    return (
        <>
            {row.competitors.value} <br/>
            {renderDescription(row.description, row.color, row.external_description)}
        </>
    );
};

export const renderStatusColumn = (column, row) => {
    return (_has(row, 'event_status.id')
        ? renderEventStatusName(row.event_status.id, row.event_status.name)
        : '');
};

export const renderProductsColumn = (column, row) => {
    let products = [];

    row.event_contents.map((event_content) => {
        event_content.products.map((product) => {
            products.push({
                short_name: product.short_name,
                id: product.product_id,
            });
        });
    });
    products.sort((a, b) => (convertToInt(a.id) > convertToInt(b.id) ? 1 : -1));

    const productsShortNames = products.map(product => product.short_name);

    return _uniqWith(productsShortNames, _isEqual).join(', ');
};

export const renderReportColumns = (column, row) => {
    const value = row[column]?.value || '';

    if (!row.currentReport || !value) {
        return null;
    }

    return (
        <ReportScopesPopup
            scopes={row.currentReport?.report_scopes_view}
            reportField={value}
            includesAllStreams={row.currentReport.includes_all_streams}
        />
    );
};

export const renderNameOfFirstLevelDependency = (row, column) => {
    return (null === row[column] || 'undefined' === typeof row[column]
        ? ''
        : row[column].name);
};

export const renderLicensees = (row) => {
    return row.licensees.map(licensee => (
        licensee.name
    )).join(', ');
};

export const renderSportOrTopCategory = (row) => {
    const sportName = _get(row, 'sport.name', ''),
        contentCategoryName = _get(row, 'content_category.name', '');

    if (!sportName && !contentCategoryName) {
        return '';
    }

    return (
        <div>
            {sportName}
            {contentCategoryName}
        </div>
    );
};

export const renderCheckbox = (row, column) => (
    !row[column] ? '' : <IconCheckmark/>
);

export const linkToPropertyLicenseEdit = (rowColumn, rowId) => (
    <Authorization authorization={authorizationObject}>
        <Link name='propertyLicences.edit' params={{id: rowId}}>
            {rowColumn}
        </Link>
    </Authorization>
);
